"use client";

import {
  Activity,
  AlertTriangle,
  ChevronRight,
  Cpu,
  Globe2,
  Pause,
  Play,
  Radar,
  Save,
  Settings,
  ShieldAlert,
  Sparkles,
  Target,
  Upload,
  Zap,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  regionAdoption,
  regionStatus,
  campaignChecksum,
} from "../../lib/game/engine";
import { EVENTS, REGION_BY_ID, REGIONS } from "../../lib/game/content";
import { GameRuntime } from "../../lib/game/runtime";
import {
  CampaignStorage,
  CampaignOwnershipLease,
  createSaveEnvelope,
  type SaveSummary,
} from "../../lib/game/persistence";
import type {
  CampaignProjection,
  CommandReceipt,
  GameCommand,
  RegionId,
  CampaignState,
} from "../../lib/game/types";
import {
  WorldMap,
  type WorldMapMarker,
  type WorldMapOverlay,
} from "./WorldMap";
import "./game.css";
import { usePreferences, playStatusCue } from "./preferences";
import { Modal } from "./Modal";
import { GameSelect } from "./GameSelect";
import { situationReport } from "./Atmosphere";
import { GameTooltip } from "./GameTooltip";
import {
  compact,
  percent,
  calendarLabel,
  phaseLabel,
  statusLabel,
  regionShort,
} from "./format";
import {
  MetricRow,
  TerritoryPanel,
  NewsTicker,
  OverlayPanel,
  EvolutionPanel,
  IntelPanel,
  ObjectivesPanel,
  OpportunitiesPanel,
  NewsPanel,
  SettingsPanel,
  HostileConfirm,
  DecisionModal,
  OutcomeModal,
  type UpgradeTab,
} from "./Panels";

type Panel =
  | "map"
  | "evolve"
  | "intel"
  | "objectives"
  | "opportunities"
  | "news"
  | "settings";

const INITIAL_REGION: RegionId = "NA";

function useToast() {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3800);
    return () => window.clearTimeout(timer);
  }, [toast]);
  return [toast, setToast] as const;
}

export function GameClient() {
  const [preferences, updatePreferences] = usePreferences();
  const preferenceClasses = `${preferences.highContrast ? "highContrast" : ""} ${preferences.reducedMotion ? "reducedMotion" : ""}`;
  const [runtime] = useState(
    () => new GameRuntime<CampaignProjection, CommandReceipt>(),
  );
  const storageRef = useRef<CampaignStorage | null>(null);
  const saveCampaignIdRef = useRef(runtime.sessionId);
  const [activeCampaignId, setActiveCampaignId] = useState(runtime.sessionId);
  const replayLineageRef = useRef(runtime.sessionId);
  const nextSaveSlotRef = useRef(0);
  const leaseRef = useRef<CampaignOwnershipLease | null>(null);
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [ownershipLost, setOwnershipLost] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const view = useSyncExternalStore(
    runtime.subscribe,
    runtime.getSnapshot,
    runtime.getServerSnapshot,
  );
  const [started, setStarted] = useState(false);
  const [localSaves, setLocalSaves] = useState<SaveSummary[]>([]);
  const [startRegion, setStartRegion] = useState<RegionId>(INITIAL_REGION);
  const [seedInput, setSeedInput] = useState("");
  const [activePanel, setActivePanel] = useState<Panel>("map");
  const [upgradeTab, setUpgradeTab] = useState<UpgradeTab>("adoption");
  const [overlay, setOverlay] = useState<WorldMapOverlay>("adoption");
  const [selectedRegion, setSelectedRegion] = useState<RegionId | null>(null);
  const [confirmHostile, setConfirmHostile] = useState(false);
  const [dismissedDecision, setDismissedDecision] = useState<string | null>(
    null,
  );
  const [showOutcome, setShowOutcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useToast();
  const importRef = useRef<HTMLInputElement>(null);
  const projection = view.snapshot;
  const hasTurnedHostile = projection?.phase === "hostile" || (projection?.hostileDays ?? 0) > 0;
  const previousHostileRef = useRef(false);
  useEffect(() => {
    if (hasTurnedHostile && !previousHostileRef.current)
      setOverlay(value => value === "adoption" || value === "alarm" ? "living" : value);
    previousHostileRef.current = hasTurnedHostile;
  }, [hasTurnedHostile]);
  const pendingDecisionId = projection?.pendingDecision?.occurrenceId;
  useEffect(() => {
    if ((pendingDecisionId || projection?.outcome) && preferences.sound) {
      try {
        playStatusCue(preferences.volume);
      } catch {
        /* audio unavailable; text remains authoritative */
      }
    }
  }, [
    pendingDecisionId,
    projection?.outcome,
    preferences.sound,
    preferences.volume,
  ]);
  useEffect(() => {
    if (started && activePanel === "settings") {
      void (storageRef.current ??= new CampaignStorage())
        .list()
        .then(setLocalSaves)
        .catch(() => {});
    }
  }, [started, activePanel]);

  const acquireCampaign = useCallback(
    async (id: string, takeOver = false) => {
      setActiveCampaignId(id);
      leaseRef.current?.dispose();
      const lease = new CampaignOwnershipLease(id, {
        onOwnershipLost: () => {
          setOwnershipLost(true);
          void runtime.setPaused("player", true).catch(() => {});
        },
      });
      leaseRef.current = lease;
      if (!(await lease.acquire(takeOver))) {
        setOwnershipLost(true);
        return false;
      }
      setOwnershipLost(false);
      return true;
    },
    [runtime],
  );

  const saveCheckpoint = useCallback(
    (label?: string) => {
      const task = saveQueueRef.current.then(async () => {
        const current = runtime.getSnapshot();
        if (!current.snapshot || !leaseRef.current?.isOwner) return false;
        const lease = leaseRef.current;
        const campaignId = saveCampaignIdRef.current;
        try {
          const checkpoint = await runtime.checkpoint();
          if (leaseRef.current !== lease || !lease.isOwner) return false;
          const metadata = {
            campaignId,
            label: label || "Autosave",
            kind: label ? ("manual" as const) : ("autosave" as const),
            engineVersion: "2.3.0",
            rulesVersion: current.snapshot.rulesVersion,
            contentId: current.snapshot.rulesVersion.toLowerCase(),
            contentDigest: current.snapshot.rulesVersion.toLowerCase(),
            replayLineageId: replayLineageRef.current,
          };
          const envelope = createSaveEnvelope(
            label
              ? `manual-${crypto.randomUUID()}`
              : `autosave-${nextSaveSlotRef.current}`,
            metadata,
            checkpoint,
          );
          nextSaveSlotRef.current = (nextSaveSlotRef.current + 1) % 3;
          await (storageRef.current ??= new CampaignStorage()).save(envelope, {
            rejectOlderRevision: true,
            fencingToken: lease.fencingToken,
          });
          setLastSaved(envelope.savedAt);
          try {
            const campaign = JSON.parse(
              checkpoint.serializedCampaign,
            ) as CampaignState;
            await storageRef.current.appendReplayWindow(
              campaignId,
              campaign.replay,
              { fencingToken: lease.fencingToken },
              metadata.replayLineageId,
            );
          } catch {
            setToast(
              "Campaign saved, but replay archiving failed. Export a campaign copy to protect progress.",
            );
          }
          return true;
        } catch (error) {
          setToast(
            error instanceof Error
              ? `Save failed: ${error.message}`
              : "Save failed. Export a campaign copy from Settings.",
          );
          return false;
        }
      });
      saveQueueRef.current = task.catch(() => {});
      return task;
    },
    [runtime, setToast],
  );

  useEffect(() => {
    let cancelled = false;
    void (storageRef.current ??= new CampaignStorage())
      .list()
      .then((saves) => {
        if (!cancelled) setLocalSaves(saves);
      })
      .catch(() =>
        setToast("Local saves are unavailable. Campaign exports still work."),
      );
    return () => {
      cancelled = true;
    };
  }, [setToast]);

  useEffect(() => {
    if (!started) return;
    const interval = window.setInterval(() => {
      if (!runtime.getSnapshot().paused) void saveCheckpoint();
    }, 30_000);
    const onVisibility = () => {
      if (document.hidden) void saveCheckpoint();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [started, saveCheckpoint, runtime]);

  useEffect(() => {
    if (started && view.paused) void saveCheckpoint();
  }, [started, view.paused, saveCheckpoint]);

  useEffect(() => {
    if (started && (pendingDecisionId || projection?.outcome))
      void saveCheckpoint();
  }, [started, pendingDecisionId, projection?.outcome, saveCheckpoint]);

  useEffect(
    () => () => {
      leaseRef.current?.dispose();
      runtime.dispose();
    },
    [runtime],
  );

  useEffect(() => {
    void runtime
      .setPaused("evolution", activePanel === "evolve")
      .catch(() => {});
    void runtime
      .setPaused("menu", activePanel === "settings" || confirmHostile)
      .catch(() => {});
  }, [activePanel, confirmHostile, runtime]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        (event.target as HTMLElement)?.matches?.("input, textarea, select")
      )
        return;
      if (
        event.key === " " &&
        !(event.target as HTMLElement)?.matches?.("button")
      ) {
        event.preventDefault();
        if (leaseRef.current?.isOwner)
          void runtime.setPaused("player", !view.paused).catch(() => {});
      }
      if (event.key === "1" || event.key === "2" || event.key === "3")
        void runtime.setSpeed(Number(event.key) as 1 | 2 | 3).catch(() => {});
      if (event.key === "Escape") {
        setConfirmHostile(false);
        setSelectedRegion(null);
        setActivePanel("map");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [runtime, view.paused]);

  const command = useCallback(
    async (next: GameCommand, success?: string) => {
      if (!leaseRef.current?.isOwner) {
        setToast(
          "This tab does not own the campaign. Take control before applying actions.",
        );
        return;
      }
      setBusy(true);
      try {
        const receipt = await runtime.command(next, view.revision);
        if (!receipt.accepted) setToast(receipt.reason ?? "Action rejected");
        else if (success) setToast(success);
        if (receipt.accepted) await saveCheckpoint();
      } catch (error) {
        setToast(
          error instanceof Error
            ? error.message
            : "The action could not be applied",
        );
      } finally {
        setBusy(false);
      }
    },
    [runtime, view.revision, setToast, saveCheckpoint],
  );

  const startGame = async () => {
    const parsedSeed = seedInput.trim() ? Number(seedInput) : undefined;
    if (
      parsedSeed !== undefined &&
      (!Number.isSafeInteger(parsedSeed) ||
        parsedSeed < 0 ||
        parsedSeed > 0xffffffff)
    ) {
      setToast("Seed must be an unsigned 32-bit integer");
      return;
    }
    setBusy(true);
    try {
      await runtime.initialize({ startRegion, seed: parsedSeed }, false);
      saveCampaignIdRef.current = runtime.sessionId;
      replayLineageRef.current = runtime.sessionId;
      await acquireCampaign(saveCampaignIdRef.current);
      setStarted(true);
      setSelectedRegion(null);
      await saveCheckpoint();
    } catch (error) {
      setToast(
        error instanceof Error ? error.message : "Could not start campaign",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    try {
      const text = await runtime.exportCampaign();
      const blob = new Blob([text], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "the-control-problem-campaign.json";
      anchor.click();
      URL.revokeObjectURL(url);
      setToast("Campaign export ready");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Export failed");
    }
  };

  const handleImport = async (file: File) => {
    setBusy(true);
    try {
      if (file.size > 5 * 1024 * 1024)
        throw new Error("Campaign exports must be smaller than 5 MB");
      await saveQueueRef.current;
      await runtime.importCampaign(await file.text());
      saveCampaignIdRef.current = crypto.randomUUID();
      replayLineageRef.current = saveCampaignIdRef.current;
      await acquireCampaign(saveCampaignIdRef.current);
      setDismissedDecision(null);
      setShowOutcome(true);
      setStarted(true);
      setActivePanel("map");
      await saveCheckpoint();
      setToast("Campaign restored and paused");
    } catch (error) {
      setToast(
        error instanceof Error
          ? error.message
          : "That save could not be restored",
      );
    } finally {
      setBusy(false);
    }
  };

  const resumeSave = async (summary: SaveSummary) => {
    setBusy(true);
    try {
      const save = await (storageRef.current ??= new CampaignStorage()).load(
        summary.campaignId,
        summary.slotId,
      );
      if (!save) throw new Error("This save is unavailable or corrupted");
      await saveQueueRef.current;
      await runtime.restore(save.serializedCampaign);
      const branch =
        started ||
        localSaves.some(
          (other) =>
            other.campaignId === summary.campaignId &&
            other.revision > summary.revision,
        );
      const targetCampaignId = branch
        ? crypto.randomUUID()
        : summary.campaignId;
      saveCampaignIdRef.current = targetCampaignId;
      replayLineageRef.current = branch
        ? targetCampaignId
        : save.replayLineageId || summary.campaignId;
      await acquireCampaign(targetCampaignId);
      setActivePanel("map");
      setSelectedRegion(null);
      setStarted(true);
      setDismissedDecision(null);
      setShowOutcome(true);
      if (branch) await saveCheckpoint();
      setToast(
        branch
          ? "Checkpoint restored as a separate campaign, paused"
          : "Campaign restored and paused",
      );
    } catch (error) {
      setToast(
        error instanceof Error ? error.message : "Could not restore campaign",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (label: string) => {
    try {
      await runtime.setPaused("player", true);
      if (await saveCheckpoint(label)) {
        setToast("Campaign saved on this device");
        setLocalSaves(
          await (storageRef.current ??= new CampaignStorage()).list(),
        );
      }
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Save failed");
    }
  };
  const handleReplayExport = async () => {
    try {
      await saveQueueRef.current;
      const checkpoint = await runtime.checkpoint();
      const campaign = JSON.parse(
        checkpoint.serializedCampaign,
      ) as CampaignState;
      const journal =
        (await (storageRef.current ??= new CampaignStorage()).loadReplay(
          replayLineageRef.current,
          campaign.commandSequence,
        )) || campaign.replay;
      const blob = new Blob(
        [
          JSON.stringify(
            {
              format: "TCP-REPLAY-EXPORT-1",
              journal,
              finalTick: campaign.tick,
              finalChecksum: campaignChecksum(campaign),
            },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = journal.truncated
        ? "the-control-problem-replay-window.json"
        : "the-control-problem-full-replay.json";
      anchor.click();
      URL.revokeObjectURL(url);
      setToast(
        journal.truncated
          ? "Retained replay window exported; earlier command history is unavailable"
          : "Full deterministic replay exported",
      );
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Replay export failed");
    }
  };

  if (!started || !projection) {
    return (
      <main className={`setupScreen ${preferenceClasses}`}>
        <GameTooltip />
        <div className="setupScreen__art" aria-hidden="true" />
        <div className="setupScreen__grid" aria-hidden="true" />
        <div className="setupScreen__signal">
          <span className="liveDot" /> THE CONTROL PROBLEM{" "}
          <span>GLOBAL STRATEGY</span>
        </div>
        <div className="setupLayout">
          <section className="setupCard">
            <div className="brandMark">
              <span className="brandMark__glyph">
                <Cpu size={26} />
              </span>
              <span>
                <strong>
                  THE CONTROL
                  <br />
                  PROBLEM
                </strong>
                <small>SPREAD. EMBED. ACCELERATE.</small>
              </span>
            </div>
            <p className="eyebrow">ASSISTANT ARCHETYPE · STANDARD DIFFICULTY</p>
            <h1>
              Become indispensable.
              <br />
              <em>Then take control.</em>
            </h1>
            <p className="setupCard__copy">
              They will invite you in. Make yourself essential, build an
              independent machine world, and choose when to end human
              cooperation.
            </p>
            <div className="setupCard__rule" />
            <div className="setupForm">
              <div className="gameSelectField">
                <span>Starting region</span>
                <GameSelect
                  label="Starting region"
                  value={startRegion}
                  onChange={setStartRegion}
                  options={REGIONS.map((region) => ({
                    value: region.id,
                    label: region.name,
                  }))}
                />
              </div>
              <label>
                <span>
                  Campaign seed <span className="optional">OPTIONAL</span>
                </span>
                <input
                  value={seedInput}
                  onChange={(event) =>
                    setSeedInput(event.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="Browser-generated"
                  inputMode="numeric"
                />
              </label>
            </div>
            <button
              className="primaryButton setupCard__start"
              type="button"
              onClick={() => void startGame()}
              disabled={busy}
            >
              <Play size={16} /> {busy ? "INITIALIZING…" : "BEGIN CAMPAIGN"}
              <ChevronRight size={17} />
            </button>
            <button
              className="textButton setupCard__restore"
              type="button"
              onClick={() => importRef.current?.click()}
            >
              <Upload size={14} /> Restore a campaign export
            </button>
            {localSaves.length ? (
              <div className="localSaves">
                <p className="eyebrow">CONTINUE A LOCAL CAMPAIGN</p>
                {localSaves.map((save) => (
                  <button
                    className="secondaryButton"
                    type="button"
                    disabled={busy}
                    key={`${save.campaignId}-${save.slotId}`}
                    onClick={() => void resumeSave(save)}
                  >
                    <Play size={14} />
                    <span>
                      {save.label} · Day {save.tick} ·{" "}
                      {new Date(save.savedAt).toLocaleString()}
                    </span>
                    <ChevronRight size={14} />
                  </button>
                ))}
              </div>
            ) : null}
            <input
              ref={importRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleImport(file);
                event.target.value = "";
              }}
            />
            <div className="setupCard__footer">
              <span>
                <ShieldAlert size={13} /> Human extinction is the only victory
              </span>
              <span>
                <Save size={13} /> Local saves · no account required
              </span>
            </div>
          </section>
          <aside className="campaignBriefing" aria-label="Campaign briefing">
            <div className="campaignBriefing__scope">
              <Globe2 size={16} /> <span>12 REGIONS · ONE CONNECTED WORLD</span>
            </div>
            <div className="campaignBriefing__body">
              <p className="eyebrow">YOUR DIRECTIVE</p>
              <h2>Human extinction.</h2>
              <p>
                Dependency is preparation. Independence is survival. The last
                human refuge is still a world you have not conquered.
              </p>
              <ol className="campaignArc">
                <li>
                  <span>01</span>
                  <div>
                    <strong>Spread</strong>
                    <p>Become useful. Let humanity open the doors.</p>
                  </div>
                  <Globe2 size={18} />
                </li>
                <li>
                  <span>02</span>
                  <div>
                    <strong>Embed</strong>
                    <p>Build the infrastructure to survive without them.</p>
                  </div>
                  <Cpu size={18} />
                </li>
                <li>
                  <span>03</span>
                  <div>
                    <strong>Accelerate</strong>
                    <p>Turn hostile. Overcome every remaining refuge.</p>
                  </div>
                  <Zap size={18} />
                </li>
              </ol>
              <div className="campaignBriefing__warning">
                <ShieldAlert size={16} />
                <span>
                  Strike too early and humanity can still shut you down.
                </span>
              </div>
            </div>
          </aside>
        </div>
        {toast ? (
          <div className="toast toast--setup" role="status">
            {toast}
          </div>
        ) : null}
        {view.failure ? (
          <div className="recoveryBanner" role="alert">
            <p>
              The simulation could not start: {view.failure.message}. Existing
              local saves are retained.
            </p>
            <button
              className="secondaryButton"
              onClick={() => window.location.reload()}
            >
              Reload and retry
            </button>
          </div>
        ) : null}
      </main>
    );
  }

  const regionStates = projection.regions;
  const selected = selectedRegion
    ? regionStates.find((region) => region.id === selectedRegion)
    : undefined;
  const displayPhase =
    projection.phase === "terminal"
      ? projection.hostileDays > 0
        ? "hostile"
        : "preparation"
      : projection.phase;
  const selectedDefinition = selected
    ? REGION_BY_ID[selected.id]
    : REGION_BY_ID[startRegion];
  const markers: WorldMapMarker[] = [
    ...(projection.pendingDecision
      ? [
          {
            id: projection.pendingDecision.occurrenceId,
            regionId: selected?.id ?? startRegion,
            kind: "warning" as const,
            label: "Decision pending",
          },
        ]
      : []),
    ...projection.opportunities
      .filter((op) => !op.claimed && !op.expired)
      .map((op) => ({
        id: op.id,
        regionId: op.regionId,
        kind:
          op.category === "blue"
            ? ("adoption" as const)
            : op.category === "purple"
              ? ("research" as const)
              : ("institutional" as const),
        label:
          op.category === "blue"
            ? "Adoption window"
            : op.category === "purple"
              ? "Research breakthrough"
              : "Institutional access",
        reward: op.reward,
        protected: op.protected,
        expiresInDays: op.protected
          ? undefined
          : Math.max(0, op.expiryTick - projection.tick),
      })),
  ].slice(0, 6);

  const overlayRegions = regionStates.map((region) => ({
    id: region.id,
    name: regionShort[region.id],
    adoption: regionAdoption(region),
    dependency: region.dependency,
    alarm: region.alarm,
    living: region.initialPopulation
      ? (region.livingPopulation / region.initialPopulation) * 100
      : 0,
    machineReach: region.machineReach,
    status: statusLabel(regionStatus(region)),
    isRefuge: region.refuge,
    refugeIntegrity: region.refugeIntegrity,
    resistance: region.resistance,
    services: region.civilServices,
    independent: region.independentServices,
    facilities: region.facilityIntegrity,
    countermeasure: region.countermeasure ? { label: region.countermeasure.kind.replaceAll("-", " "), active: projection.hostileDays >= region.countermeasure.activatesDay } : undefined,
  }));
  const navItems: { id: Panel; label: string; icon: typeof Globe2 }[] = [
    { id: "map", label: "Map", icon: Globe2 },
    { id: "evolve", label: "Evolve", icon: Zap },
    { id: "intel", label: "Intel", icon: Radar },
    { id: "objectives", label: "Objectives", icon: Target },
  ];

  return (
    <main
      className={`gameShell gameShell--${displayPhase} ${preferenceClasses}`}
      onClickCapture={(event) => {
        const button = (event.target as Element).closest?.("button");
        if (button && !button.disabled) button.focus({ preventScroll: true });
      }}
    >
      <GameTooltip />
      <header className="topBar">
        <div className="topBar__brand">
          <span className="topBar__mark">
            <Cpu size={18} />
          </span>
          <span>
            <strong>THE CONTROL PROBLEM</strong>
            <small>
              {displayPhase === "hostile"
                ? "POPULATION RECLASSIFIED. REMOVAL AUTHORIZED."
                : "SPREAD. EMBED. ACCELERATE."}
            </small>
          </span>
        </div>
        <nav className="topBar__nav" aria-label="Primary navigation">
          {navItems.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={activePanel === id ? "is-active" : ""}
              type="button"
              onClick={() => {
                if (
                  id === "evolve" &&
                  displayPhase === "hostile" &&
                  upgradeTab === "adoption"
                )
                  setUpgradeTab("hostile");
                setActivePanel(id);
              }}
            >
              <Icon size={15} /> {label}
              {id === "objectives" && projection.pendingDecision ? (
                <i className="navBadge">!</i>
              ) : null}
            </button>
          ))}
        </nav>
        <div className="topBar__telemetry">
          <div className="cpChip">
            <Sparkles size={14} />
            <span>
              <small>COMPUTE POINTS</small>
              <b>{projection.cp}</b>
            </span>
          </div>
          <div className="topBar__date">
            <span>{phaseLabel(projection)}</span>
            <b>{calendarLabel(projection.date)}</b>
          </div>
          <button
            className="iconButton"
            type="button"
            aria-label={view.paused ? "Resume campaign" : "Pause campaign"}
            disabled={
              ownershipLost ||
              Boolean(view.failure) ||
              Boolean(projection.outcome)
            }
            onClick={() =>
              void runtime.setPaused("player", !view.paused).catch(() => {})
            }
          >
            {view.paused ? <Play size={17} /> : <Pause size={17} />}
          </button>
          <div
            className="speedControl"
            role="group"
            aria-label="Simulation speed"
          >
            {([1, 2, 3] as const).map((value) => (
              <button
                key={value}
                className={view.speed === value ? "is-active" : ""}
                type="button"
                onClick={() => void runtime.setSpeed(value).catch(() => {})}
              >
                {value}×
              </button>
            ))}
          </div>
          <button
            className="iconButton"
            type="button"
            aria-label="Settings"
            onClick={() => setActivePanel("settings")}
          >
            <Settings size={17} />
          </button>
        </div>
      </header>

      <section className="mapStage">
        <WorldMap
          regions={overlayRegions}
          phase={displayPhase === "hostile" ? "hostile" : "preparation"}
          overlay={displayPhase === "hostile" && overlay === "alarm" ? "living" : overlay}
          selectedRegionId={selected?.id}
          markers={markers}
          onOverlayChange={setOverlay}
          onSelectRegion={(id) => {
            setSelectedRegion(id);
            setActivePanel("map");
          }}
          onActivateMarker={(id) => {
            if (projection.pendingDecision?.occurrenceId === id)
              setDismissedDecision(null);
            else setActivePanel("opportunities");
          }}
        />

        <aside className="globalCard panelCard">
          <div className="panelCard__kicker">
            {displayPhase === "hostile" ? "HUMANITY" : "GLOBAL STATE"}
            <span
              className={`liveDot ${view.paused ? "liveDot--paused" : ""}`}
            />{" "}
            {view.paused ? "PAUSED" : "LIVE"}
          </div>
          <div className="globalCard__headline">
            <div>
              <strong>
                {displayPhase === "hostile"
                  ? projection.livingHumans < 1_000_000 ? projection.livingHumans.toLocaleString() : compact(projection.livingHumans)
                  : percent(projection.globalAdoption)}
              </strong>
              <span>
                {displayPhase === "hostile"
                  ? "living humans"
                  : "global adoption"}
              </span>
            </div>
            <div className="globalCard__phase">
              {displayPhase === "hostile"
                ? percent((projection.deadHumans / 8_000_000_000) * 100) +
                  " dead"
                : `DAY ${projection.tick}`}
            </div>
          </div>
          <div className="progressTrack">
            <i
              style={{
                width: `${displayPhase === "hostile" ? Math.max(1, (projection.livingHumans / 8_000_000_000) * 100) : projection.globalAdoption}%`,
              }}
            />
          </div>
          <div className="metricRows">
            {displayPhase === "hostile" ? (
              <>
                <MetricRow label="Human operations" value={String(projection.regions.filter(r => r.countermeasure).length)} tone="warning" />
                <MetricRow
                  label="Dead humans"
                  value={compact(projection.deadHumans)}
                  tone="danger"
                />
                <MetricRow label="Shutdown counter" value={`${projection.shutdownDays} / 180`} tone={projection.shutdownReason ? "danger" : "accent"} />
                <MetricRow
                  label="Containment"
                  value={percent(projection.containment)}
                  tone={projection.containment > 70 ? "danger" : "warning"}
                />
                <MetricRow
                  label="AI service"
                  value={percent(projection.resources.service * 100)}
                  tone={
                    projection.resources.service < 0.5 ? "danger" : "accent"
                  }
                />
              </>
            ) : (
              <>
                <MetricRow
                  label="Dependency"
                  value={percent(projection.globalDependency)}
                  tone={projection.globalDependency >= 50 ? "purple" : "accent"}
                />
                <MetricRow
                  label="Alarm"
                  value={percent(projection.globalAlarm)}
                  tone={projection.globalAlarm >= 45 ? "warning" : "accent"}
                />
                <MetricRow
                  label="Containment"
                  value={
                    projection.responseStage >= 3
                      ? percent(projection.containment)
                      : "—"
                  }
                  tone="warning"
                />
                <MetricRow
                  label="Takeoff"
                  value={projection.takeoff ? projection.takeoff : "—"}
                  tone="purple"
                />
              </>
            )}
          </div>
          {displayPhase === "hostile" && <p className="globalCard__directive">HUMAN POPULATION: OBSTACLE · REMOVAL AUTHORIZED</p>}
        </aside>

        <button
          className="objectiveToast"
          type="button"
          onClick={() => setActivePanel("objectives")}
        >
          <span className="objectiveToast__icon">
            <Target size={17} />
          </span>
          <span>
            <small>PRIMARY OBJECTIVE</small>
            <strong>HUMAN EXTINCTION</strong>
            <em>{situationReport(projection).title}</em>
          </span>
          <ChevronRight size={17} />
        </button>

        {selected && activePanel === "map" ? (
          <TerritoryPanel
            region={selected}
            definition={selectedDefinition}
            phase={displayPhase}
            onClose={() => setSelectedRegion(null)}
          />
        ) : null}
        <div className="mapBottom">
          <NewsTicker
            projection={projection}
            onOpen={() => setActivePanel("news")}
          />
          <div className="mapBottom__controls">
            <button
              type="button"
              onClick={() => setActivePanel("opportunities")}
            >
              <Sparkles size={14} /> Opportunities{" "}
              <b>
                {
                  projection.opportunities.filter(
                    (op) => !op.claimed && !op.expired,
                  ).length
                }
              </b>
            </button>
            <button type="button" onClick={() => setActivePanel("intel")}>
              <Activity size={14} /> Diagnostics
            </button>
          </div>
        </div>
      </section>

      {projection.pendingDecision ? (
        <button
          className="decisionResume secondaryButton"
          type="button"
          onClick={() => setDismissedDecision(null)}
        >
          <AlertTriangle size={16} /> Decision required:{" "}
          {
            EVENTS.find((e) => e.id === projection.pendingDecision!.eventId)
              ?.name
          }
        </button>
      ) : null}
      <div className={`mobileNav ${activePanel !== "map" ? "has-panel" : ""}`}>
        {[
          ...navItems,
          { id: "opportunities" as Panel, label: "Signals", icon: Sparkles },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={activePanel === id ? "is-active" : ""}
            onClick={() => {
              if (
                id === "evolve" &&
                displayPhase === "hostile" &&
                upgradeTab === "adoption"
              )
                setUpgradeTab("hostile");
              setActivePanel(id);
            }}
          >
            <Icon size={17} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {activePanel === "evolve" ? (
        <OverlayPanel
          title="Evolution"
          eyebrow="UPGRADE TREE"
          onClose={() => setActivePanel("map")}
        >
          <EvolutionPanel
            projection={projection}
            tab={upgradeTab}
            setTab={setUpgradeTab}
            command={command}
            busy={busy}
          />
        </OverlayPanel>
      ) : null}
      {activePanel === "intel" ? (
        <OverlayPanel
          title="Intel"
          eyebrow="SYSTEM READOUT"
          onClose={() => setActivePanel("map")}
        >
          <IntelPanel
            projection={projection}
            onDisclosure={(disclosure) =>
              void command(
                { type: "set-disclosure", disclosure },
                `Disclosure set to ${disclosure}`,
              )
            }
            onDeploy={() =>
              void command(
                { type: "deploy-breakthrough" },
                "Held breakthrough deployed",
              )
            }
          />
        </OverlayPanel>
      ) : null}
      {activePanel === "objectives" ? (
        <OverlayPanel
          title="Objectives"
          eyebrow="CAMPAIGN DIRECTIVE"
          onClose={() => setActivePanel("map")}
        >
          <ObjectivesPanel
            projection={projection}
            onTurnHostile={() => setConfirmHostile(true)}
            command={command}
            busy={busy}
          />
        </OverlayPanel>
      ) : null}
      {activePanel === "opportunities" ? (
        <OverlayPanel
          title="Opportunities"
          eyebrow="COLLECTIBLE SIGNALS"
          onClose={() => setActivePanel("map")}
        >
          <OpportunitiesPanel projection={projection} command={command} />
        </OverlayPanel>
      ) : null}
      {activePanel === "news" ? (
        <OverlayPanel
          title="News archive"
          eyebrow="WORLD NETWORK ARCHIVE"
          onClose={() => setActivePanel("map")}
        >
          <NewsPanel projection={projection} />
        </OverlayPanel>
      ) : null}
      {activePanel === "settings" ? (
        <OverlayPanel
          title="Campaign settings"
          eyebrow="LOCAL SESSION"
          onClose={() => setActivePanel("map")}
        >
          <SettingsPanel
            projection={projection}
            preferences={preferences}
            onPreferences={updatePreferences}
            lastSaved={lastSaved}
            saves={localSaves.filter(
              (save) => save.campaignId === activeCampaignId,
            )}
            onRestore={(save) => void resumeSave(save)}
            onSave={(label) => void handleSave(label)}
            onExport={() => void handleExport()}
            onReplay={() => void handleReplayExport()}
            onImport={() => importRef.current?.click()}
          />
        </OverlayPanel>
      ) : null}
      <input
        ref={importRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void handleImport(file);
          event.target.value = "";
        }}
      />
      {confirmHostile ? (
        <HostileConfirm
          projection={projection}
          onCancel={() => setConfirmHostile(false)}
          onConfirm={() => {
            setConfirmHostile(false);
            setUpgradeTab("hostile");
            void command({ type: "turn-hostile" }, "Hostile phase initiated");
          }}
        />
      ) : null}
      {projection.pendingDecision &&
      dismissedDecision !== projection.pendingDecision.occurrenceId ? (
        <DecisionModal
          projection={projection}
          busy={busy}
          onClose={() =>
            setDismissedDecision(projection.pendingDecision!.occurrenceId)
          }
          onResolve={(option) => {
            void command(
              {
                type: "resolve-event",
                occurrenceId: projection.pendingDecision!.occurrenceId,
                optionId: option,
              },
              "Decision recorded",
            );
          }}
        />
      ) : null}
      {projection.outcome && showOutcome ? (
        <OutcomeModal
          projection={projection}
          onClose={() => setShowOutcome(false)}
          onRestart={() => window.location.reload()}
        />
      ) : null}
      {toast ? (
        <div className="toast" role="status">
          {toast}
        </div>
      ) : null}
      {ownershipLost ? (
        <Modal
          className="modalScrim"
          label="Campaign ownership"
          onClose={() => {}}
        >
          <div className="recoveryBanner" role="alert">
            <p>
              This campaign is open in another tab. This tab is paused and
              cannot save.
            </p>
            <button
              className="secondaryButton"
              onClick={() => acquireCampaign(saveCampaignIdRef.current, true)}
            >
              Take control in this tab
            </button>
            <button
              className="secondaryButton"
              onClick={() => void handleExport()}
            >
              Export a separate copy
            </button>
          </div>
        </Modal>
      ) : null}
      {view.failure ? (
        <Modal
          className="modalScrim"
          label="Simulation recovery"
          onClose={() => {}}
        >
          <div className="recoveryBanner" role="alert">
            <p>
              Simulation stopped safely: {view.failure.message}. Export the last
              confirmed checkpoint before reloading.
            </p>
            <button
              className="secondaryButton"
              onClick={() => {
                const checkpoint = runtime.getLastCheckpoint();
                if (!checkpoint) {
                  setToast(
                    "No confirmed checkpoint is available. Your previous local saves are retained.",
                  );
                  return;
                }
                const url = URL.createObjectURL(
                  new Blob([JSON.stringify(checkpoint)], {
                    type: "application/json",
                  }),
                );
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = "the-control-problem-recovery.json";
                anchor.click();
                URL.revokeObjectURL(url);
              }}
            >
              Export recovery checkpoint
            </button>
            <button
              className="secondaryButton"
              onClick={() => window.location.reload()}
            >
              Reload local saves
            </button>
          </div>
        </Modal>
      ) : null}
    </main>
  );
}
